/**
 * Core-100 R4 — CORE-Y: what a CSV import does with each row, decided in one pure place.
 *
 *  - IDENTITY (CORE-Y-GAP-005). A row's `import_row_key` is its identity within the shop: the scheduled day plus the
 *    RO number, or — when the row has none — the day plus a digest of the row's content. The database admits a key
 *    once among live tickets (`tickets_csv_import_row_once`), so a double submit or a second tab cannot create a
 *    ticket twice; the client pre-check is only a courtesy.
 *  - EVERY ROW IS REPORTED ("an import MUST report what happened to every row"): a row that fails is a failed row
 *    with its reason, a row the database already holds is "already imported" — never silently dropped.
 *  - NO SILENT COMMITMENT — OD-40 (founder decision, REVISED 2026-10-15). Every created ticket arrives unassigned
 *    (awaiting assignment) and is placed through OptiPlace: the import's placement review queue opens the existing
 *    OptiPlace recommendation for one ticket at a time; the advisor accepts the recommended technician / date /
 *    start, picks another valid one, or defers. The former opt-in "assign automatically" (smart-assign's #1 placed
 *    without anyone seeing it) is removed.
 *  - COMPLETED HISTORY STAYS HISTORY (OD-40): a row whose status says the work is finished is not turned into new
 *    active workload; it is reported (shop history belongs to the Historical Import).
 *  - MISSING INFORMATION IS NAMED (OD-40): a row without a ticket number, customer name or any job is reported with
 *    exactly what is missing and not created — nothing is invented to fill it; correct the CSV and import again
 *    (the row key makes a re-import safe).
 */

export const CSV_IMPORT_AWAITING_ASSIGNMENT_REASON = 'awaiting-assignment';
export const CSV_IMPORT_UNIQUE_INDEX = 'tickets_csv_import_row_once';

export interface CsvRowIdentityInput {
  ticketNumber?: string | null;
  customerName?: string | null;
  customerPhone?: string | null;
  year?: number | null;
  make?: string | null;
  model?: string | null;
  vin?: string | null;
  jobs?: Array<{ name?: string | null; minutes?: number | null }> | null;
}

async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

const t = (v: unknown) => String(v ?? '').trim();

/** The row's identity within the shop for this scheduled day. Deterministic: the same row on the same day → same key. */
export async function csvImportRowKey(row: CsvRowIdentityInput, scheduledDay: string): Promise<string> {
  const ro = t(row.ticketNumber);
  if (ro) return `csv:${scheduledDay}:ro:${ro.toUpperCase().slice(0, 120)}`;
  const content = JSON.stringify([
    t(row.customerName).toLowerCase(), t(row.customerPhone).replace(/\D/g, ''), row.year ?? null,
    t(row.make).toLowerCase(), t(row.model).toLowerCase(), t(row.vin).toUpperCase(),
    (row.jobs ?? []).map((j) => [t(j.name).toLowerCase(), j.minutes ?? null]),
  ]);
  return `csv:${scheduledDay}:row:${(await sha256Hex(content)).slice(0, 40)}`;
}

export interface ImportRowOutcome {
  status: 'success' | 'warning' | 'failed';
  message: string;
}

/** A row the create step refused: already imported (the unique key) is a warning; anything else failed, with why. */
export function classifyImportRowError(err: unknown): ImportRowOutcome {
  const e = (err ?? {}) as { code?: string; message?: string; details?: string };
  const text = `${e.message ?? ''} ${e.details ?? ''}`;
  if (e.code === '23505' && text.includes(CSV_IMPORT_UNIQUE_INDEX)) {
    return { status: 'warning', message: 'Already imported for this day (not created again)' };
  }
  const reason = (e.message ?? (err instanceof Error ? err.message : String(err ?? ''))).trim();
  return { status: 'failed', message: reason ? `Not created: ${reason}` : 'Not created (no reason was returned)' };
}

/** The outcome reported for a row that was created: unassigned until the advisor places it through OptiPlace (OD-40). */
export function awaitingAssignmentOutcome(): ImportRowOutcome {
  return { status: 'success', message: 'Created — awaiting placement (review it with OptiPlace below, or defer)' };
}

/** CSV statuses that mean the work is already finished (OD-40: imported history, not new active workload). */
const COMPLETED_STATUS = /^(completed?|closed|done|finished|picked[\s_-]*up|delivered|invoiced|paid)$/i;

export type ImportRowPlacementClass =
  | { kind: 'schedulable' }
  | { kind: 'historical'; status: string }
  | { kind: 'missing_information'; missing: string[] };

/** Decide, before anything is written, whether a parsed row becomes active (schedulable) work. Pure. */
export function classifyImportRowForPlacement(row: { status?: string | null; missingInfo?: string[] | null }): ImportRowPlacementClass {
  const status = t(row.status);
  if (status && COMPLETED_STATUS.test(status)) return { kind: 'historical', status };
  const missing = (row.missingInfo ?? []).filter(Boolean);
  if (missing.length > 0) return { kind: 'missing_information', missing };
  return { kind: 'schedulable' };
}

/** The reported outcome of a row that was NOT created as active work. */
export function notCreatedOutcome(c: Exclude<ImportRowPlacementClass, { kind: 'schedulable' }>): ImportRowOutcome {
  if (c.kind === 'historical') {
    return {
      status: 'warning',
      message: `Not added to the schedule: status "${c.status}" is completed work — imported history is not new workload (use Historical Import for shop history)`,
    };
  }
  return {
    status: 'failed',
    message: `Not created — missing: ${c.missing.join(', ')}. Correct the row and import again, or leave it out (re-importing never duplicates a ticket)`,
  };
}
